import { useLayoutEffect, useRef, useState } from "react";
import type { AssignmentCriterion, CriteriaTemplateItem, GradeLevel, PaperType } from "@markeroo/shared";
import { mutedText, smallMutedText} from "@/styles/variants";
import { buttonVariants } from "@markeroo/theme";
import { ui } from "@/strings";
import { CriteriaPill } from "./CriteriaPill";
import PiPlusDefaultStroke from "@markeroo/icons/src/stroke/maths/PiPlusDefaultStroke";

/**
 * Common criterion type that works for both AssignmentCriterion and CriteriaTemplateItem.
 * Both types have id, key, and label which is all we need for the list.
 */
export type CriterionItem = AssignmentCriterion | CriteriaTemplateItem;

interface CriteriaListProps<T extends CriterionItem = CriterionItem> {
  criteria: T[];
  onEdit: (criterion: T) => void;
  onDelete: (criterionKey: string) => void;
  onAddClick?: () => void;
  isDeleting?: boolean;
  highlightIds?: Set<string>; // IDs to highlight with shimmer animation (new, matched, or updated)
  paperType?: PaperType | null;
  gradeLevel?: GradeLevel | null;
}

export function CriteriaList<T extends CriterionItem>({ criteria, onEdit, onDelete, onAddClick, isDeleting = false, highlightIds, paperType, gradeLevel }: CriteriaListProps<T>) {
  const [newCriteriaIds, setNewCriteriaIds] = useState<Set<string>>(new Set());
  const prevCriteriaIdsRef = useRef<Set<string>>(new Set());
  const isInitialMountRef = useRef(true);

  useLayoutEffect(() => {
    const currentIds = new Set(criteria.map(c => c.id));

    // On initial mount, just set the ref and skip animation
    if (isInitialMountRef.current) {
      prevCriteriaIdsRef.current = currentIds;
      isInitialMountRef.current = false;
      return;
    }

    const prevIds = prevCriteriaIdsRef.current;

    // Find IDs that are in current but not in previous (newly added)
    const newIds = new Set<string>();
    currentIds.forEach(id => {
      if (!prevIds.has(id)) {
        newIds.add(id);
      }
    });

    if (newIds.size > 0) {
      setNewCriteriaIds(newIds);

      // Remove the "new" status after 60 seconds
      const timeout = setTimeout(() => {
        setNewCriteriaIds(new Set());
      }, 60000);

      return () => clearTimeout(timeout);
    }

    // Update the ref for next comparison
    prevCriteriaIdsRef.current = currentIds;
  }, [criteria]);

  // When highlightIds is provided, override the automatic detection
  useLayoutEffect(() => {
    if (highlightIds && highlightIds.size > 0) {
      setNewCriteriaIds(highlightIds);

      // Remove the "new" status after 60 seconds
      const timeout = setTimeout(() => {
        setNewCriteriaIds(new Set());
      }, 60000);

      return () => clearTimeout(timeout);
    }
  }, [highlightIds]);

  if (criteria.length === 0) {
    return (
      <div data-tutorial-target="criteria-list" className="flex flex-col items-center justify-center py-12 text-center">
        <p className={mutedText}>{ui.empty.noCriteria}</p>
        <p className={smallMutedText}>{ui.labels.criteriaGeneratorHint}</p>
        {onAddClick && (
          <button
            type="button"
            className={`${buttonVariants.primary} mt-4`}
            onClick={onAddClick}
          >
            {ui.actions.addCriteria}
          </button>
        )}
      </div>
    );
  }

  return (
    <div data-tutorial-target="criteria-list" className="flex flex-col gap-6">
      {/* Section header - Figma spec: flex justify-between, specific typography */}
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-h3 leading-6 tracking-tight text-text-theme-primary">
          {ui.headers.activeCriteria} {"("}
          {criteria.length}
          {")"}
        </h3>
        {onAddClick && (
          <button
            type="button"
            className={`${buttonVariants.primary} gap-1.5`}
            onClick={onAddClick}
          >
            <PiPlusDefaultStroke size={20} />
            {ui.actions.addCriteria}
          </button>
        )}
      </div>
      {/* Criteria pills - flex-wrap layout */}
      <div className="flex flex-wrap gap-y-5 gap-x-2.5">
        {criteria.map((criterion) => (
          <CriteriaPill
            key={criterion.id}
            criterion={criterion}
            onEdit={() => onEdit(criterion)}
            onDelete={() => onDelete(criterion.key)}
            isDeleting={isDeleting}
            isNew={newCriteriaIds.has(criterion.id)}
            paperType={paperType}
            gradeLevel={gradeLevel}
          />
        ))}
      </div>
    </div>
  );
}
